<script lang="ts">
import { computed, defineComponent, ref } from 'vue';

export default defineComponent({
  name: 'FunctionResultTable',
  props: {
    results: { type: Object, required: true },
    title: { type: String, default: 'Function Analysis' },
    isSax: { type: Boolean, default: true },
    indexMode: {
      type: String,
      default: 'BSA', // initial mode: 'BSA' | 'height'
      validator: (v: string) => ['BSA', 'height'].includes(v),
    },
  },
  setup(props) {
    // Local toggle state, seeded from the prop. Clicking the indexed
    // column header switches between per-BSA and per-height.
    const activeIndexMode = ref(props.indexMode);
    const toggleIndexMode = () => {
      activeIndexMode.value
        = activeIndexMode.value === 'BSA' ? 'height' : 'BSA';
    };

    const displayData = computed(() => {
      const {
        EDV,
        ESV,
        SV,
        EF,
        CO,
        HeartRateMean,
        diastolicMyoMass,
        systolicMyoMass,
        diastolicPapMass,
        systolicPapMass,
        RVEDV,
        RVESV,
        RVSV,
        RVEF,
        GLSEndo,
        GLSEpi,
        LFSEndo,
        LFSEpi,
        CFSEndo,
        CFSEpi,
        diastolicPhase,
        systolicPhase,
        diastolicPhaseRV,
        systolicPhaseRV,
      } = props.results;

      // Base data that's always shown
      const data = {
        EDV,
        ESV,
        SV,
        EF,
        CO,
        HeartRateMean,
        diastolicMyoMass,
        systolicMyoMass,
      };

      // Conditionally add items based on view type
      if (props.isSax) {
        // Add SAX-specific items (RV-related)
        Object.assign(data, {
          diastolicPapMass,
          systolicPapMass,
          diastolicPhase,
          systolicPhase,
          RVEDV,
          RVESV,
          RVSV,
          RVEF,
          diastolicPhaseRV,
          systolicPhaseRV,
          CFSEndo,
          CFSEpi,
        });
      }
      else {
        // Add LAX-specific items (GLS-related)
        Object.assign(data, {
          diastolicPhase,
          systolicPhase,
          GLSEndo,
          GLSEpi,
          LFSEndo,
          LFSEpi,
        });
      }

      return data;
    });

    const indexedKeyFor = (key: string, mode: string): string | null => {
      const suffix = mode === 'height' ? 'indH' : 'indBSA';
      // CO is special: its BSA index is CI, and there is no height index
      if (key === 'CO') return mode === 'BSA' ? 'CI' : null;
      // Only volume metrics have indexed variants
      const indexable = [
        'EDV',
        'ESV',
        'SV',
        'RVEDV',
        'RVESV',
        'RVSV',
        'systolicMyoMass',
        'diastolicMyoMass',
      ];
      return indexable.includes(key) ? `${key}${suffix}` : null;
    };

    const getIndexedUnit = (key: string, mode: string): string => {
      if (key === 'CO') return 'l/min/m²'; // CI
      if (key.includes('MyoMass')) return mode === 'height' ? 'g/m' : 'g/m²';
      return mode === 'height' ? 'ml/m' : 'ml/m²'; // indexed volumes
    };

    const formatIndexedValue = (key: string): string => {
      const idxKey = indexedKeyFor(key, activeIndexMode.value);
      if (!idxKey) return '';
      const v = props.results[idxKey];
      // Show a dash so the column stays visually present when demographics
      // (BSA / height) are unavailable and the indexed value is 0.
      if (typeof v !== 'number' || !Number.isFinite(v) || v === 0) return '-';
      return `${v.toFixed(1)} ${getIndexedUnit(key, activeIndexMode.value)}`;
    };

    // Create a computed property for groupDividers that depends on isSax
    const groupDividers = computed(() => {
      // Dividers that are always present
      const dividers = ['EF', 'HeartRateMean', 'systolicMyoMass', 'systolicPhase'];

      // Conditionally add dividers based on view type
      if (props.isSax) {
        dividers.push('RVEF');
      }
      else {
        dividers.push('GLSEpi');
      }

      return dividers;
    });

    const formatLabel = (key: string): string => {
      const labels: Record<string, string> = {
        EF: 'EF',
        SV: 'SV',
        EDV: 'EDV',
        ESV: 'ESV',
        CO: 'Cardiac Output',
        CI: 'Cardiac Index',
        HeartRateMean: 'Heart Rate',
        systolicMyoMass: 'MyoMass Systolic',
        diastolicMyoMass: 'MyoMass Diastolic',
        systolicPapMass: 'PapMass Systolic',
        diastolicPapMass: 'PapMass Diastolic',
        RVEF: 'RV EF',
        RVSV: 'RV SV',
        RVEDV: 'RV EDV',
        RVESV: 'RV ESV',
        diastolicPhase: 'Diastolic Phase',
        systolicPhase: 'Systolic Phase',
        diastolicPhaseRV: 'Diastolic Phase RV',
        systolicPhaseRV: 'Systolic Phase RV',
        GLSEndo: 'GLS Endo',
        GLSEpi: 'GLS Epi',
        LFSEndo: 'LFS Endo',
        LFSEpi: 'LFS Epi',
        CFSEndo: 'CFS Endo',
        CFSEpi: 'CFS Epi',
      };

      return labels[key] || key;
    };

    const getUnitForKey = (key: string): string => {
      if (
        key.includes('EF')
        || key.includes('GLS')
        || key.includes('CFS')
        || key.includes('LFS')
      ) {
        return '%';
      }
      if (key.includes('SV') || key.includes('EDV') || key.includes('ESV'))
        return 'ml';
      if (key.includes('Mass')) return 'g';
      if (key.includes('HeartRate')) return '/min';
      if (key.includes('CO')) return 'l/min';
      if (key.includes('CI')) return 'l/min/m²';
      return '';
    };

    const formatValue = (value: any, key: string): string => {
      if (typeof value === 'number') {
        if (value === Infinity) return '0.0 ml'; // only esv

        // Check if this is a phase value - display as integer
        if (key.includes('Phase')) {
          // Special case: display dash for -1 phase values
          if (value === -1) return '-';
          return `${Math.round(value)}`;
        }

        // Heart rate - display as a rounded integer in bpm
        if (key.includes('HeartRate')) {
          if (value === 0) return '-';
          return `${Math.round(value)} /min`;
        }

        // Other numeric values - display with decimal places
        return `${value.toFixed(1)} ${getUnitForKey(key)}`;
      }
      return String(value);
    };

    const isGroupDivider = (key: string): boolean => {
      return groupDividers.value.includes(key);
    };

    return {
      activeIndexMode,
      toggleIndexMode,
      displayData,
      formatLabel,
      formatValue,
      getUnitForKey,
      isGroupDivider,
      groupDividers,
      formatIndexedValue,
    };
  },
});
</script>

<template>
  <div class="function-result-table">
    <div class="table-content">
      <table>
        <thead>
          <tr class="header-tr">
            <th class="result-label header-cell">
              {{ title }}
            </th>
            <th class="result-value header-cell" />
            <th class="result-value indexed header-cell">
              <button
                type="button"
                class="index-toggle-btn"
                :title="`Switch to ${activeIndexMode === 'height' ? 'per BSA' : 'per height'}`"
                @click="toggleIndexMode"
              >
                {{ activeIndexMode === 'height' ? '/ height' : '/ BSA' }}
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(value, key) in displayData"
            :key="key"
            class="result-row"
            :class="{ 'group-divider': isGroupDivider(key) }"
          >
            <td class="result-label" :title="formatLabel(key)">
              {{ formatLabel(key) }}
            </td>
            <td class="result-value" :title="formatValue(value, key)">
              {{ formatValue(value, key) }}
            </td>
            <td class="result-value indexed" :title="formatIndexedValue(key)">
              {{ formatIndexedValue(key) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.function-result-table {
  position: relative;
  width: fit-content;
  min-width: 300px; /* room for label + value + indexed columns */
  flex: 0 0 auto; /* don't shrink below content inside the flex row */
  border: 1px solid var(--md-surface-container-highest);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--md-surface-container);
  display: flex;
  flex-direction: column;
}

.table-content {
  width: 100%;
}

table {
  width: 100%;
  border-collapse: collapse;
}

.header-tr {
  background-color: var(--md-surface-container-high);
  border-bottom: 1px solid var(--md-surface-container-highest);
}

.header-cell {
  height: 28px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0 8px;
}

th.result-label {
  text-align: left;
}

th.result-value {
  text-align: right;
}

/* indexed column header hosts the toggle button */
th.result-value.indexed {
  padding: 2px 6px;
}

.index-toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 22px;
  padding: 0 8px;
  font-size: 0.7rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--md-on-surface, inherit);
  background-color: var(--md-surface-container-highest);
  border: 1px solid var(--md-outline, var(--md-surface-container-highest));
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition:
    background-color 0.15s ease,
    filter 0.15s ease;
}

.index-toggle-btn:hover {
  filter: brightness(1.15);
}

.index-toggle-btn:active {
  filter: brightness(0.95);
}

.index-toggle-btn:focus-visible {
  outline: 2px solid var(--md-info, #4a90d9);
  outline-offset: 1px;
}

.result-row {
  transition: background-color 0.15s ease;
}

.result-row.group-divider {
  border-bottom: 2px solid var(--md-surface-container-highest);
}

.result-row:last-child {
  border-bottom: none;
}

.result-row:hover {
  background-color: var(--md-surface-container-highest);
}

.result-label {
  padding: 6px 8px;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 28px;
  border-right: 1px solid var(--md-surface-container-highest);
  border-bottom: 1px solid var(--md-surface-container-highest);
  width: 80px;
}

.result-value {
  padding: 6px 12px 6px 8px;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 28px;
  width: 80px;
  font-family: monospace;
  text-align: right;
  border-bottom: 1px solid var(--md-surface-container-highest);
}

.result-value.indexed {
  border-left: 1px solid var(--md-surface-container-highest);
}

tbody tr:last-child td {
  border-bottom: none;
}
</style>
